import * as React from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DatePickerProps {
  value: string; // yyyy-mm-dd
  onChange: (date: string) => void;
  minDate?: string;
  placeholder?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  placeholder = 'Selecione a data'
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const dateValue = value ? parseISO(value) : undefined;

  const handleSelect = (selectedDay: Date | undefined) => {
    if (!selectedDay) return;
    const yyyy = selectedDay.getFullYear();
    const mm = String(selectedDay.getMonth() + 1).padStart(2, '0');
    const dd = String(selectedDay.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
  };

  const handleToday = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
  };

  const handleClear = () => {
    onChange('');
  };

  const formattedDate = dateValue && isValid(dateValue)
    ? format(dateValue, 'dd/MM/yyyy')
    : '';

  const minDateObj = minDate ? parseISO(minDate) : undefined;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-between text-left font-normal h-11 px-3 rounded-xl border border-gray-200 hover:border-green-500 hover:bg-white focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors',
            !value && 'text-muted-foreground'
          )}
        >
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            {value ? (
              <span className="text-gray-900">{formattedDate}</span>
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronUp className={cn('h-4 w-4 text-gray-400 transition-transform', isOpen && 'rotate-180')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl shadow-lg border border-gray-100" align="start">
        <div className="space-y-3 p-4 overflow-hidden rounded-2xl">
          <Calendar
            locale={ptBR}
            mode="single"
            selected={dateValue}
            onSelect={handleSelect}
            disabled={minDateObj ? { before: minDateObj } : undefined}
            initialFocus
            fixedWeeks
          />
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToday}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 text-sm font-medium"
            >
              Hoje
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 text-sm font-medium"
            >
              Limpar
            </Button>
          </div>


        </div>
      </PopoverContent>
    </Popover>
  );
};
